// Shared font-size-application logic used by:
// - The EditorJS `FontSizeTool` (`editorjs-tools/font-size/font-size-tool.ts`) inside
//   paragraph clusters.
// - The custom selection toolbar (`hooks/use-selection-toolbar.tsx`) used
//   on container titles via `EditableText`.
// Both code paths must produce the SAME DOM output (`<span style="font-size:Npt">…</span>`)
// so a paragraph copy/pasted into a title (or vice versa) stays consistent
// across the sanitizer + re-render round trip. Mirrors `text-color-ops.ts`.

import { dispatchInputEventOnEditableHost, reselectBetweenNodes } from './text-color-ops';

const FONT_SIZE_RE = /^\d+pt$/;

export const DEFAULT_FONT_SIZE_PALETTE: readonly number[] = [
  9, 10, 11, 12, 14, 16, 18, 24,
];

// Module-level fallback: the most recent size the user picked. Used as the
// swatch label when the current selection isn't itself inside a font-size
// span — mirrors `lastPickedColor` in `text-color-ops.ts`.
let lastPickedFontSize: number | null = null;

export function getLastPickedFontSize(): number | null {
  return lastPickedFontSize;
}

export function setLastPickedFontSize(pt: number | null): void {
  if (pt === null) {
    lastPickedFontSize = null;
    return;
  }
  if (!Number.isInteger(pt) || pt <= 0) return;
  lastPickedFontSize = pt;
}

/**
 * Wrap `range` in `<span style="font-size:Npt">…</span>`.
 * Returns `false` if `pt` is not a positive integer or if the DOM mutation
 * throws (invalid range). On success the range's contents are replaced by
 * the wrapper span, the wrapper's contents become the live selection (so a
 * caller re-reading `window.getSelection()` immediately after this call gets
 * a fresh, non-collapsed range instead of the collapsed range left behind by
 * `extractContents`), and `lastPickedFontSize` is updated.
 *
 * If `range` exactly spans the full contents of an existing lone
 * font-size span, that span's style is mutated in place instead of wrapping
 * a new span around it — avoids nesting spans when the user picks a
 * different size repeatedly in the same session (this is exactly what
 * happens on the 2nd+ pick, since the 1st pick's reselect above leaves the
 * live selection as precisely that span's contents).
 */
export function applyFontSizeToRange(range: Range, pt: number): boolean {
  if (!Number.isInteger(pt) || pt <= 0) return false;
  const existing = findExactWrappingFontSizeSpan(range);
  if (existing) {
    existing.setAttribute('style', `font-size:${pt}pt`);
    setLastPickedFontSize(pt);
    dispatchInputEventOnEditableHost(existing);
    reselectElementContents(existing);
    return true;
  }
  const span = document.createElement('span');
  span.setAttribute('style', `font-size:${pt}pt`);
  try {
    span.appendChild(range.extractContents());
    range.insertNode(span);
  } catch {
    // extractContents or insertNode can throw if the range is invalid.
    return false;
  }
  setLastPickedFontSize(pt);
  dispatchInputEventOnEditableHost(span);
  reselectElementContents(span);
  return true;
}

/**
 * Returns the nearest ancestor SPAN of `range.startContainer` when it is a
 * lone font-size span (per `getFontSizeFromSpan`) AND `range` exactly spans
 * that span's full contents — i.e. the selection IS that span, not merely
 * a sub-range or overlapping range. Used to replace a size in place rather
 * than nesting a new span around an existing one.
 */
function findExactWrappingFontSizeSpan(range: Range): HTMLElement | null {
  if (range.collapsed) return null;
  const startEl =
    range.startContainer.nodeType === Node.TEXT_NODE
      ? range.startContainer.parentElement
      : (range.startContainer as HTMLElement | null);
  if (!startEl || getFontSizeFromSpan(startEl) === null) return null;
  const spanRange = document.createRange();
  spanRange.selectNodeContents(startEl);
  const startsMatch =
    range.compareBoundaryPoints(Range.START_TO_START, spanRange) === 0;
  const endsMatch =
    range.compareBoundaryPoints(Range.END_TO_END, spanRange) === 0;
  return startsMatch && endsMatch ? startEl : null;
}

/**
 * Replace the live browser selection with one spanning `el`'s contents.
 * Called after a DOM mutation so a caller re-reading `window.getSelection()`
 * immediately afterward gets a fresh, correct range — `Range#extractContents`
 * collapses whatever range it's called on, so a cached pre-mutation Range
 * reference is useless for a subsequent apply/remove in the same session.
 */
function reselectElementContents(el: HTMLElement): void {
  const sel = window.getSelection();
  if (!sel) return;
  const newRange = document.createRange();
  newRange.selectNodeContents(el);
  sel.removeAllRanges();
  sel.addRange(newRange);
}

export function isCaretInsideFontSizedSpan(): boolean {
  return findFontSizeAncestorOfSelection() !== null;
}

/**
 * Read a font size from a span's INLINE style. Returns the integer point
 * size, or `null` if the span:
 *   - has no inline font-size, OR
 *   - has *additional* inline styles (security: don't preserve unknown
 *     CSS — a paste could carry e.g. `background:url(javascript:…)` next
 *     to a benign font-size).
 */
export function getFontSizeFromSpan(el: HTMLElement): number | null {
  if (el.tagName !== 'SPAN') return null;
  const fontSizeValue = el.style.fontSize;
  if (!fontSizeValue) return null;
  // Reject spans that carry more than one declared property — we only round-
  // trip the pure single-property font-size case.
  if (el.style.length !== 1 || el.style.item(0) !== 'font-size') return null;
  return normaliseToPt(fontSizeValue);
}

/**
 * Walk up from `node` looking for the nearest SPAN ancestor whose only
 * inline style is `font-size`. Returns that span or null. Excludes
 * `.ref-chip` because chip spans use the same tag but are semantically
 * references.
 */
function findFontSizeAncestor(node: Node | null): HTMLElement | null {
  let cur: Node | null = node;
  while (cur) {
    if (cur.nodeType === Node.ELEMENT_NODE) {
      const el = cur as HTMLElement;
      if (el.tagName === 'SPAN' && !el.classList.contains('ref-chip')) {
        const pt = getFontSizeFromSpan(el);
        if (pt !== null) return el;
      }
    }
    cur = cur.parentNode;
  }
  return null;
}

function findFontSizeAncestorOfSelection(): HTMLElement | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  return findFontSizeAncestor(sel.getRangeAt(0).startContainer);
}

/**
 * Font size currently applied at the selection start — for toolbar display.
 * Returns null when the caret isn't inside a font-sized span; callers may
 * fall through to `getLastPickedFontSize()` for the visual "active" hint.
 */
export function getSelectionFontSize(): number | null {
  const ancestor = findFontSizeAncestorOfSelection();
  if (!ancestor) return null;
  return getFontSizeFromSpan(ancestor);
}

/**
 * Strip font-size formatting around `range` by unwrapping the nearest
 * font-sized-span ancestor of its start container. v1 limitation: removes
 * the size from the ENTIRE span's contents, not just the selected
 * sub-range — matches Word/Google Docs reset behaviour. Returns `true` if a
 * span was unwrapped.
 *
 * Takes a Range explicitly (rather than reading `window.getSelection()`)
 * so callers can capture the user's intended range at click-time and not
 * race with focus/selection clearing that the click event may have
 * triggered in the meantime.
 */
export function removeFontSizeFromRange(range: Range): boolean {
  const ancestor = findFontSizeAncestor(range.startContainer);
  if (!ancestor) return false;
  const firstChild = ancestor.firstChild;
  const lastChild = ancestor.lastChild;
  const ok = unwrapElement(ancestor);
  if (!ok) return false;
  if (firstChild) {
    dispatchInputEventOnEditableHost(firstChild);
    reselectBetweenNodes(firstChild, lastChild ?? firstChild);
  }
  return true;
}

function unwrapElement(el: HTMLElement): boolean {
  const parent = el.parentNode;
  if (!parent) return false;
  while (el.firstChild) {
    parent.insertBefore(el.firstChild, el);
  }
  parent.removeChild(el);
  return true;
}

/**
 * Normalise a browser `style.fontSize` value (commonly `Npt`, sometimes
 * `Npx` if the browser re-emits in a different unit) to an integer point
 * size. Returns `null` if it can't be expressed as an integer `pt` value.
 */
function normaliseToPt(value: string): number | null {
  const trimmed = value.trim().toLowerCase();
  if (FONT_SIZE_RE.test(trimmed)) return parseInt(trimmed, 10);
  return null;
}

// Exported for tests so we can assert normalisation handling without
// re-instantiating the whole tool.
export const __test = { normaliseToPt };
